import React from 'react';

interface InputProps {
  id?: string;
  label?: string;
  type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'date' | 'time' | 'datetime-local' | 'month';
  placeholder?: string;
  value: string | number;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  helperText?: string;
  icon?: string;
  iconPosition?: 'left' | 'right';
  className?: string;
  fullWidth?: boolean;
  maxLength?: number;
  minLength?: number;
  min?: string;
  max?: string;
  step?: string;
  // type="number" only: decimal places allowed (0 = whole numbers). Default 2.
  decimals?: number;
  autoComplete?: string;
}

// Strips everything except digits and a single decimal point, and caps the
// integer and decimal parts. Native type="number" inputs let the browser
// display raw invalid text (letters, repeated +/-/e) that a sanitizing
// onChange can't visually clear, so numeric fields render as type="text"
// (below) and this sanitizer's result always matches what's on screen.
const MAX_INTEGER_DIGITS = 10;

const sanitizeNumeric = (raw: string, decimals: number): string => {
  const cleaned = raw.replace(decimals > 0 ? /[^0-9.]/g : /[^0-9]/g, '');
  const firstDot = cleaned.indexOf('.');
  const intPart = (firstDot === -1 ? cleaned : cleaned.slice(0, firstDot)).slice(0, MAX_INTEGER_DIGITS);
  // Collapse leading zeros ("007" -> "7") but keep a lone "0" and "0.5".
  const normalizedInt = intPart.replace(/^0+(?=\d)/, '');
  if (firstDot === -1) return normalizedInt;
  const decPart = cleaned.slice(firstDot + 1).replace(/\./g, '').slice(0, decimals);
  return `${normalizedInt || '0'}.${decPart}`;
};

export default function Input({
  id,
  label,
  type = 'text',
  placeholder,
  value,
  onChange,
  onBlur,
  required = false,
  disabled = false,
  error,
  helperText,
  icon,
  iconPosition = 'left',
  className = '',
  fullWidth = true,
  maxLength,
  minLength,
  min,
  max,
  step,
  decimals = 2,
  autoComplete,
}: InputProps) {
  const widthClass = fullWidth ? 'w-full' : '';
  const errorClass = error ? 'border-red-500 focus:ring-red-500' : 'border-ink-200 focus:ring-brand-500';
  const disabledClass = disabled ? 'bg-ink-50 cursor-not-allowed' : 'bg-white';
  const isNumeric = type === 'number';
  const errorId = id ? `${id}-error` : undefined;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isNumeric) {
      const sanitized = sanitizeNumeric(e.target.value, decimals);
      if (sanitized !== e.target.value) e.target.value = sanitized;
    }
    onChange(e);
  };

  return (
    <div className={`${widthClass} ${className}`}>
      {label && (
        <label htmlFor={id} className="block text-xs font-bold uppercase tracking-[0.06em] text-ink-600 mb-1.5">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <div className="relative">
        {icon && iconPosition === 'left' && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <i className={`fas ${icon} text-ink-400 text-sm`}></i>
          </div>
        )}
        <input
          id={id}
          type={isNumeric ? 'text' : type}
          inputMode={isNumeric ? (decimals > 0 ? 'decimal' : 'numeric') : undefined}
          placeholder={placeholder}
          value={value}
          onChange={handleChange}
          onBlur={onBlur}
          required={required}
          disabled={disabled}
          maxLength={maxLength}
          minLength={minLength}
          min={isNumeric ? undefined : min}
          max={isNumeric ? undefined : max}
          step={isNumeric ? undefined : step}
          autoComplete={autoComplete}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          className={`
            ${widthClass}
            ${icon && iconPosition === 'left' ? 'pl-10' : ''}
            ${icon && iconPosition === 'right' ? 'pr-10' : ''}
            px-3 py-2 border rounded-md min-h-[44px] lg:min-h-0 text-sm
            focus:outline-none focus:ring-2 focus:border-transparent
            transition-colors duration-150 text-ink-900
            ${errorClass}
            ${disabledClass}
          `}
        />
        {icon && iconPosition === 'right' && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <i className={`fas ${icon} text-ink-400 text-sm`}></i>
          </div>
        )}
      </div>
      {error && (
        <p id={errorId} role="alert" className="mt-1 text-xs text-red-600">
          <i className="fas fa-circle-exclamation mr-1" />
          {error}
        </p>
      )}
      {!error && helperText && <p className="mt-1 text-xs text-ink-500">{helperText}</p>}
    </div>
  );
}
